/**
 * Battle transitions — framework-agnostic helpers for dramatic
 * overworld <-> battle scene swaps and encounter banner reveals.
 *
 * Pure TS. No React, no DOM mutation. Callers apply className/style
 * to their elements and drive progress via the runTransition tick.
 *
 * SSR-safe: when window is undefined, runTransition fires onComplete
 * synchronously and returns a no-op cancel.
 */

export type TransitionKind =
  | 'wipe-down'       // black bar slides top -> bottom (200ms)
  | 'wipe-diagonal'   // diagonal black wipe (300ms)
  | 'spiral'          // radial-gradient pinch from edges to center (500ms)
  | 'flash-white'     // full white flash, fades out (250ms)
  | 'pixelate-in';    // chunky stepped pixel reveal (400ms, 12 steps)

export type Transition = {
  kind: TransitionKind;
  durationMs: number;
  onHalfway?: () => void;
  onComplete?: () => void;
};

export type TransitionStyle = {
  containerClassName: string;
  containerStyle: Record<string, string | number>;
};

/** Default durations (ms) per transition kind. Callers may override. */
export const TRANSITION_DEFAULT_MS: Record<TransitionKind, number> = {
  'wipe-down': 200,
  'wipe-diagonal': 300,
  spiral: 500,
  'flash-white': 250,
  'pixelate-in': 400,
};

/** Discrete steps for the chunky pixelate-in reveal. */
const PIXELATE_STEPS = 12;

/** Encounter banner total duration. */
export const ENCOUNTER_BANNER_MS = 1200;

/** Clamp helper — pure, no deps. */
function clamp01(n: number): number {
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

/** ease-in-out cubic — smooth, no bounce, feels natural for wipes. */
function easeInOutCubic(t: number): number {
  const x = clamp01(t);
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}

/** Stepped curve — snaps progress to N discrete buckets for chunky pixel feel. */
function steppedProgress(t: number, steps: number): number {
  const x = clamp01(t);
  // Snap to bottom of bucket: produces visible "jumps" rather than smooth fade.
  return Math.floor(x * steps) / steps;
}

/**
 * Returns className + inline-style for the transition overlay at a given
 * progress (0..1). Caller composes these onto a full-viewport overlay div.
 */
export function getTransitionClasses(
  t: Transition,
  progress: number,
): TransitionStyle {
  const p = clamp01(progress);

  switch (t.kind) {
    case 'wipe-down': {
      const eased = easeInOutCubic(p);
      const pct = (eased * 100).toFixed(2);
      return {
        containerClassName: 'battle-transition battle-transition--wipe-down',
        containerStyle: {
          position: 'fixed',
          inset: 0,
          background: '#000',
          transform: `translateY(${(-100 + eased * 100).toFixed(2)}%)`,
          // Expose progress for any consumers that want to react.
          ['--transition-progress' as string]: pct,
          pointerEvents: 'none',
          zIndex: 9999,
        },
      };
    }

    case 'wipe-diagonal': {
      const eased = easeInOutCubic(p);
      // Diagonal clip from top-left corner sweeping to bottom-right.
      const cut = (eased * 200).toFixed(2);
      return {
        containerClassName: 'battle-transition battle-transition--wipe-diagonal',
        containerStyle: {
          position: 'fixed',
          inset: 0,
          background: '#000',
          clipPath: `polygon(0 0, ${cut}% 0, 0 ${cut}%)`,
          pointerEvents: 'none',
          zIndex: 9999,
        },
      };
    }

    case 'spiral': {
      const eased = easeInOutCubic(p);
      // Radial pinch: visible (black) ring shrinks from outside toward center.
      // At p=0 the whole screen is transparent; at p=1 the whole screen is black.
      const outerPct = (100 - eased * 100).toFixed(2);
      const innerPct = (Math.max(0, 100 - eased * 100 - 8)).toFixed(2);
      return {
        containerClassName: 'battle-transition battle-transition--spiral',
        containerStyle: {
          position: 'fixed',
          inset: 0,
          background: `radial-gradient(circle at center, transparent ${innerPct}%, #000 ${outerPct}%)`,
          pointerEvents: 'none',
          zIndex: 9999,
        },
      };
    }

    case 'flash-white': {
      // Sharp rise to full white, ease-out fade. Peak at ~30% progress.
      const peak = 0.3;
      let opacity: number;
      if (p < peak) {
        opacity = p / peak;
      } else {
        opacity = 1 - (p - peak) / (1 - peak);
      }
      return {
        containerClassName: 'battle-transition battle-transition--flash-white',
        containerStyle: {
          position: 'fixed',
          inset: 0,
          background: '#fff',
          opacity: clamp01(opacity).toFixed(3),
          pointerEvents: 'none',
          zIndex: 9999,
        },
      };
    }

    case 'pixelate-in': {
      // Stepped reveal: caller renders the destination scene with a CSS
      // pixel-size filter; we expose the step index + chunk size.
      const stepped = steppedProgress(p, PIXELATE_STEPS);
      // Pixel chunk size: 32px at p=0 down to 1px at p=1.
      const chunkPx = Math.max(1, Math.round(32 * (1 - stepped)));
      return {
        containerClassName: 'battle-transition battle-transition--pixelate-in',
        containerStyle: {
          position: 'fixed',
          inset: 0,
          background: '#000',
          opacity: (1 - stepped).toFixed(3),
          ['--pixel-chunk' as string]: `${chunkPx}px`,
          ['--pixel-step' as string]: Math.round(stepped * PIXELATE_STEPS),
          pointerEvents: 'none',
          zIndex: 9999,
        },
      };
    }
  }
}

export type TransitionHandle = {
  cancel: () => void;
};

/**
 * Drives a transition via requestAnimationFrame. Calls onTick(progress)
 * each frame. Fires onHalfway once at progress >= 0.5 and onComplete
 * once at progress >= 1.0.
 *
 * SSR-safe: when window is undefined, fires onComplete immediately
 * and returns a no-op cancel.
 */
export function runTransition(
  t: Transition,
  onTick: (progress: number) => void,
): TransitionHandle {
  if (typeof window === 'undefined') {
    t.onComplete?.();
    return { cancel: () => {} };
  }

  const duration = Math.max(1, t.durationMs);
  const start = performance.now();
  let rafId = 0;
  let cancelled = false;
  let halfwayFired = false;
  let completeFired = false;

  const tick = (now: number): void => {
    if (cancelled) return;
    const progress = clamp01((now - start) / duration);
    onTick(progress);

    if (!halfwayFired && progress >= 0.5) {
      halfwayFired = true;
      t.onHalfway?.();
    }

    if (progress >= 1) {
      if (!completeFired) {
        completeFired = true;
        t.onComplete?.();
      }
      return;
    }

    rafId = window.requestAnimationFrame(tick);
  };

  rafId = window.requestAnimationFrame(tick);

  return {
    cancel: () => {
      cancelled = true;
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
    },
  };
}

export type EncounterBannerStage = {
  text: string;
  atMs: number;
};

/**
 * Three-beat encounter banner: punctuation hit, "A wild", species name.
 * Total duration is ENCOUNTER_BANNER_MS; stages fire at the start of
 * each third so the caller can render whichever stage has the latest
 * atMs <= elapsed.
 */
export function buildEncounterBannerStages(
  speciesName: string,
): EncounterBannerStage[] {
  return [
    { text: '!', atMs: 0 },
    { text: 'A wild', atMs: 300 },
    { text: `${speciesName} appeared!`, atMs: 600 },
  ];
}
